import { sha512 } from 'sha512-crypt-ts';

import type { DeviceProfile } from './profiles';
import { resolveLuksPassword, type ProvisionFormValues } from './validation';

const saltAlphabet = './0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

function yamlString(value: string): string {
  return JSON.stringify(value);
}

export function createCryptSalt(length = 16): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));

  return Array.from(bytes, (byte) => saltAlphabet[byte % saltAlphabet.length]).join('');
}

export function generateAutoinstall(
  values: ProvisionFormValues,
  profile: DeviceProfile,
  salt = createCryptSalt(),
): string {
  const loginPasswordHash = sha512.crypt(values.loginPassword, salt);
  const codecs = values.installCodecs ? 'true' : 'false';
  const luksPassword = resolveLuksPassword(values);
  const storagePassword = luksPassword
    ? `      password: ${yamlString(luksPassword)}\n`
    : '';

  return `#cloud-config
# Gerado localmente por provision.shud.com.br. Contém segredos: não versionar.
autoinstall:
  version: 1

  locale: pt_BR.UTF-8
  keyboard:
    layout: br
    variant: ""
  timezone: America/Sao_Paulo

  identity:
    realname: ${yamlString(values.realName.trim())}
    hostname: ${yamlString(profile.hostname)}
    username: ${yamlString(values.username)}
    password: ${yamlString(loginPasswordHash)}

  source:
    id: ubuntu-desktop
    search_drivers: true

  drivers:
    install: true
  codecs:
    install: ${codecs}
  oem:
    install: auto

  network:
    version: 2
    renderer: NetworkManager
    wifis:
      ${profile.wifi.interfaceName}:
        dhcp4: true
        access-points:
          ${yamlString(values.wifiSsid)}:
            password: ${yamlString(values.wifiPassword)}

  storage:
    layout:
      name: lvm
      sizing-policy: all
${storagePassword}      match:
        ${profile.disk.match.key}: ${yamlString(profile.disk.match.value)}

  ssh:
    install-server: true
    allow-pw: false
    authorized-keys:
      - ${yamlString(values.sshPublicKey.trim())}

  packages:
    - ca-certificates
    - curl
    - gh
    - git
    - language-pack-pt
    - openssh-server

  updates: security
  shutdown: reboot
`;
}

export function redactAutoinstall(content: string): string {
  return content.replace(/^(\s+password:)\s+.+$/gm, '$1 "••••••••"');
}

export function downloadAutoinstall(content: string, filename = 'autoinstall.yaml'): void {
  const blob = new Blob([content], { type: 'application/yaml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
